// src/share/WhatsAppPicker.jsx
//
// Account picker modal that shows EVERY time a user clicks "Share to
// WhatsApp" anywhere in the app. Two-step:
//
//   Step 1 — pick account: [Personal] / [Business] / Cancel
//   Step 2 — if that account's number isn't stored yet, prompt for it
//            inline. Save → return to Step 1's resolution.
//
// On resolution, the parent's onResolve(number) callback fires with the
// chosen number (E.164). Parent uses it to construct the wa.me URL OR (for
// the share-OWN-content flow) just opens wa.me/?text=… — the number is
// stored for record-keeping + future public-profile click target use.
//
// Props:
//   isOpen      bool — show/hide
//   onClose     ()  — user cancelled the picker
//   onResolve   (chosen) — chosen = { type, number }
//                         Called once both steps complete. Parent then
//                         performs the actual share.
//
// Loads the user's stored numbers via GET /api/social/whatsapp/numbers
// when the modal opens. Stores newly-entered numbers via POST same path.

import React, { useEffect, useState } from 'react';
import { authFetch } from '../utils/authFetch';

const API_URL = process.env.REACT_APP_API_URL || '';

// Light client-side normalization to mirror server's normalizeE164. Lets
// the user type "(814) 886-3385" or "1-814-886-3385" and we coerce to
// "+18148863385" before submitting. Server is the source of truth — if our
// client normalization disagrees, server validates.
function normalizeE164ClientSide(raw) {
  if (raw == null) return '';
  let s = String(raw).trim().replace(/[\s\-().]/g, '');
  if (s && /^\d/.test(s)) s = '+' + s;
  return s;
}

function isLikelyValidE164(s) {
  return /^\+\d{7,15}$/.test(String(s || ''));
}

export default function WhatsAppPicker({ isOpen, onClose, onResolve }) {
  // Current step: 'pick' (choose Personal/Business) or 'enter' (input number)
  const [step, setStep] = useState('pick');
  const [pickedType, setPickedType] = useState(null); // 'personal' | 'business'
  const [numbers, setNumbers] = useState({ personal: null, business: null });
  const [loading, setLoading] = useState(false);
  const [numberInput, setNumberInput] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset to step 1 every time the modal opens. Stale state from a previous
  // open (an aborted entry, an error message) shouldn't carry over.
  useEffect(() => {
    if (!isOpen) return;
    setStep('pick');
    setPickedType(null);
    setNumberInput('');
    setError('');
    setSaving(false);

    // Fetch the user's stored numbers so we know whether to skip the entry
    // step on pick. Cheap — single row lookup on users.
    setLoading(true);
    (async () => {
      try {
        const r = await authFetch('/api/social/whatsapp/numbers', { method: 'GET' });
        const data = await r.json().catch(() => ({}));
        if (r.ok && data?.ok) {
          setNumbers({
            personal: data.personal || null,
            business: data.business || null,
          });
        } else {
          // Not fatal — user can still enter inline. Treat as "no numbers stored."
          setNumbers({ personal: null, business: null });
        }
      } catch {
        setNumbers({ personal: null, business: null });
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen]);

  const handlePick = (type) => {
    const stored = numbers[type];
    if (stored) {
      // Number already on file — straight through to share.
      onResolve({ type, number: stored });
      return;
    }
    // First time picking this type — collect the number.
    setPickedType(type);
    setStep('enter');
    setNumberInput('');
    setError('');
  };

  const handleSaveNumber = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setError('');
    const normalized = normalizeE164ClientSide(numberInput);
    if (!isLikelyValidE164(normalized)) {
      setError('Please enter a valid number with country code, e.g. +1 814 886 3385');
      return;
    }
    // Optimistic resolve. Critical: window.open inside the parent's
    // onResolve handler relies on the user-activation token from THIS click.
    // If we await the save first, browsers consume the activation and block
    // the wa.me popup. So: resolve synchronously, save in the background.
    // On rare server-save failure, the user will be asked to enter the
    // number again next time — recoverable. The share itself succeeds.
    setNumbers((prev) => ({ ...prev, [pickedType]: normalized }));
    onResolve({ type: pickedType, number: normalized });

    authFetch('/api/social/whatsapp/numbers', {
      method: 'POST',
      body: JSON.stringify({ type: pickedType, number: normalized }),
    }).catch((err) => {
      console.warn('[WhatsAppPicker] background number save failed:', err);
    });
  };

  const handleBack = () => {
    setStep('pick');
    setPickedType(null);
    setNumberInput('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        zIndex: 100100, // above the existing post modal (z-index 100000)
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // CRITICAL: the parent Sharing Center wrapper sets
        // pointer-events: none to let clicks pass through its empty
        // chrome to underlying app content. Children that need clicks
        // must opt back in. Without this, every click on the picker
        // passes through to whatever's behind it — making the picker
        // look open but be functionally dead.
        pointerEvents: 'auto',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose && onClose(); }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          boxShadow: '0 18px 48px rgba(0,0,0,0.22)',
          padding: '20px 22px',
          width: 380,
          maxWidth: '92vw',
          border: '1px solid #e2e8f0',
        }}
        role="dialog"
        aria-label="Choose WhatsApp account"
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" style={{ flex: 'none' }}>
              <rect width="24" height="24" rx="5" fill="#25D366"/>
              <path d="M19.7 4.3C17.7 2.3 15 1.2 12.1 1.2 6.3 1.2 1.6 5.9 1.6 11.7c0 1.9.5 3.7 1.4 5.3L1.5 22.8l5.9-1.5c1.5.8 3.2 1.3 5 1.3 5.8 0 10.5-4.7 10.5-10.5 0-2.8-1.1-5.4-3.2-7.4z" fill="#fff"/>
            </svg>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
              {step === 'pick' ? 'Share to WhatsApp' : `Add your ${pickedType} number`}
            </h3>
          </div>
          <button
            type="button"
            onClick={() => { if (!saving) onClose && onClose(); }}
            disabled={saving}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 18, color: '#94a3b8', lineHeight: 1, padding: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Step 1: pick account */}
        {step === 'pick' && (
          <>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
              Which account are you posting from? You'll send from whichever WhatsApp you're currently logged into on this device.
            </p>
            {loading ? (
              <p style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>
                Loading…
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => handlePick('business')}
                  style={{
                    padding: '12px 14px',
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: 8,
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 22 }}>💼</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Business</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {numbers.business ? numbers.business : 'Add your business WhatsApp'}
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handlePick('personal')}
                  style={{
                    padding: '12px 14px',
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: 8,
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 22 }}>📱</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Personal</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {numbers.personal ? numbers.personal : 'Add your personal WhatsApp'}
                    </div>
                  </div>
                </button>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button
                type="button"
                onClick={() => onClose && onClose()}
                style={{
                  padding: '7px 14px',
                  fontSize: 13,
                  background: 'white',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  color: '#475569',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {/* Step 2: enter number for the picked type */}
        {step === 'enter' && (
          <form onSubmit={handleSaveNumber}>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
              Enter your <strong>{pickedType}</strong> WhatsApp number with country code. We'll save it so you don't have to enter it again.
            </p>
            <input
              type="tel"
              value={numberInput}
              onChange={(e) => { setNumberInput(e.target.value); if (error) setError(''); }}
              placeholder="+1 814 886 3385"
              autoFocus
              disabled={saving}
              style={{
                width: '100%',
                padding: '8px 10px',
                fontSize: 14,
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                outline: 'none',
                marginBottom: 10,
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
            {error && (
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#dc2626' }}>
                {error}
              </p>
            )}
            <p style={{ margin: '0 0 14px', fontSize: 11, color: '#94a3b8', lineHeight: 1.4 }}>
              To remove or change a number later, visit your account settings.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleBack}
                disabled={saving}
                style={{
                  padding: '7px 14px',
                  fontSize: 13,
                  background: 'white',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  color: '#475569',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={saving || !numberInput.trim()}
                style={{
                  padding: '7px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  background: saving || !numberInput.trim() ? '#94a3b8' : '#25D366',
                  border: 'none',
                  borderRadius: 6,
                  color: 'white',
                  cursor: saving || !numberInput.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Saving…' : 'Save & Continue'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
